import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Download, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useDeleteVendorContact,
  useUpsertVendorContact,
  useVendorContacts,
} from "@/features/vendors/api";
import { useVendorId } from "@/features/vendors/detail/use-vendor-id";
import {
  contactFormSchema,
  emptyContactForm,
  type ContactFormValues,
} from "@/features/vendors/schema";
import type { VendorContact } from "@/features/vendors/types";
import { cn } from "@/lib/utils";

export function VendorContactsPage() {
  const vendorId = useVendorId();
  const { data: contacts = [], isLoading, refetch, isFetching } = useVendorContacts(vendorId);
  const upsert = useUpsertVendorContact(vendorId);
  const remove = useDeleteVendorContact(vendorId);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<VendorContact | null>(null);
  const [deleting, setDeleting] = useState<VendorContact | null>(null);
  const [search, setSearch] = useState("");

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: emptyContactForm,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      editing
        ? {
            name: editing.name,
            designation: editing.designation ?? "",
            department: editing.department ?? "",
            mobile: editing.mobile ?? "",
            alt_mobile: editing.alt_mobile ?? "",
            email: editing.email ?? "",
            is_primary: editing.is_primary,
            is_active: editing.is_active,
          }
        : { ...emptyContactForm, is_primary: contacts.length === 0 },
    );
  }, [open, editing, form, contacts.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.email, c.mobile, c.designation, c.department]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [contacts, search]);

  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
    setEditing(null);
  });

  function startCreate() {
    setEditing(null);
    setOpen(true);
  }

  function exportCsv() {
    const header = ["name", "designation", "department", "mobile", "email", "primary", "active"];
    const lines = [
      header.join(","),
      ...filtered.map((c) =>
        [
          csv(c.name),
          csv(c.designation ?? ""),
          csv(c.department ?? ""),
          c.mobile ?? "",
          c.email ?? "",
          c.is_primary ? "1" : "0",
          c.is_active ? "1" : "0",
        ].join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts-${vendorId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <Button type="button" size="sm" onClick={startCreate}>
          <Plus />
          New contact
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!filtered.length}
          >
            <Download />
            Export
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Refresh"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn(isFetching && "animate-spin")} />
          </Button>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="h-8 w-44 pl-7"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-xs text-muted-foreground">Loading…</p>
      ) : contacts.length === 0 ? (
        <div className="p-6">
          <EmptyState
            title="No contacts yet"
            description="Add purchase, accounts and quality contacts for this vendor."
            action={
              <Button type="button" size="sm" onClick={startCreate}>
                <Plus />
                New contact
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Full name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={7} className="py-10 text-center text-xs text-muted-foreground">
                    No contacts match “{search}”.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((contact) => (
                  <TableRow key={contact.id} className="group">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <AvatarInitials name={contact.name} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{contact.name}</span>
                            {contact.is_primary ? <Badge variant="default">Primary</Badge> : null}
                          </div>
                          {contact.department ? (
                            <div className="truncate text-2xs text-muted-foreground">
                              {contact.department}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                          {contact.email}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{contact.designation || "—"}</TableCell>
                    <TableCell>
                      {contact.mobile ? (
                        <a href={`tel:${contact.mobile}`} className="hover:text-primary">
                          {contact.mobile}
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{contact.department || "—"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={contact.is_active}
                        onCheckedChange={(v) =>
                          void upsert.mutateAsync({
                            id: contact.id,
                            values: {
                              name: contact.name,
                              designation: contact.designation ?? "",
                              department: contact.department ?? "",
                              mobile: contact.mobile ?? "",
                              alt_mobile: contact.alt_mobile ?? "",
                              email: contact.email ?? "",
                              is_primary: contact.is_primary,
                              is_active: v,
                            },
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Edit contact"
                          onClick={() => {
                            setEditing(contact);
                            setOpen(true);
                          }}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Delete contact"
                          className="hover:text-destructive"
                          onClick={() => setDeleting(contact)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-2xs text-muted-foreground">
            <span>
              Showing {filtered.length ? 1 : 0} to {filtered.length} of {filtered.length} entries
            </span>
          </div>
        </>
      )}

      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing ? "Edit contact" : "New contact"}</SheetTitle>
            <SheetDescription>People you deal with at this supplier.</SheetDescription>
          </SheetHeader>

          <form id="vendor-contact-form" onSubmit={onSubmit} className="flex-1 overflow-y-auto p-4">
            <FieldGrid>
              <Field label="Full name" required span={12} error={errors.name?.message}>
                <Input {...form.register("name")} autoFocus />
              </Field>
              <Field label="Designation" span={6}>
                <Input {...form.register("designation")} placeholder="Sales manager" />
              </Field>
              <Field label="Department" span={6}>
                <Input {...form.register("department")} placeholder="Sales" />
              </Field>
              <Field label="Mobile" span={6}>
                <Input {...form.register("mobile")} inputMode="tel" />
              </Field>
              <Field label="Alternate" span={6}>
                <Input {...form.register("alt_mobile")} inputMode="tel" />
              </Field>
              <Field label="Email" span={12} error={errors.email?.message}>
                <Input {...form.register("email")} type="email" />
              </Field>
              <ToggleField
                label="Primary contact"
                description="Shown on documents"
                span={6}
                checked={form.watch("is_primary")}
                onChange={(v) => form.setValue("is_primary", v, { shouldDirty: true })}
              />
              <ToggleField
                label="Active"
                span={6}
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v, { shouldDirty: true })}
              />
            </FieldGrid>
          </form>

          <SheetFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="vendor-contact-form" size="sm" disabled={upsert.isPending}>
              {editing ? "Save changes" : "Add contact"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete contact?"
        description={deleting ? `${deleting.name} will be removed from this vendor.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}

function AvatarInitials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-2xs font-semibold text-primary">
      {initials || "?"}
    </span>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
